import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Button from "../../ui/buttons/Button";
import IconButton from "../../ui/buttons/IconButton";
import rocket from "../../assets/icons/rocket.svg";
import rocketHover from "../../assets/icons/rocket-hover.svg";
import { templatesService } from "../../services/templates.service.js";
import { accountsService } from "../../services/accounts.service.js";
import FloatingModal from "../../ui/Modals/FloatingModal";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput.jsx";
import DropdownSelect from "../../ui/dropdowns/DropdownSelect.jsx";
import { ConfirmDeleteModal } from "../admin/AdminDetailModals.jsx";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import styles from "../../styles/components/templates/TemplateGroupModal.module.css";


const NAME_PLACEHOLDER = "Nombre del grupo de plantillas";
const NAME_FIELD_MIN_WIDTH = 240;
const NAME_FIELD_MAX_WIDTH = 560;
// Padding interno del input + margen para el caret.
const NAME_FIELD_EXTRA_WIDTH = 40;

function TemplateGroupModal({ onClose, onTemplateGroupCreated, onNewTemplateGroupError, initialData }) {
  const isEditing = Boolean(initialData?.id);
  const [rocketHovered, setRocketHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [name, setName] = useState(initialData?.name ?? "");

  // El ancho del campo (y con él, el del modal) sigue al texto: se mide con
  // un span oculto que usa la misma tipografía que el input de BaseInput.
  const nameSizerRef = useRef(null);
  const [nameFieldWidth, setNameFieldWidth] = useState(NAME_FIELD_MIN_WIDTH);

  useLayoutEffect(() => {
    const measuredTextWidth = nameSizerRef.current?.offsetWidth ?? 0;
    setNameFieldWidth(
      Math.min(
        Math.max(NAME_FIELD_MIN_WIDTH, measuredTextWidth + NAME_FIELD_EXTRA_WIDTH),
        NAME_FIELD_MAX_WIDTH,
      ),
    );
  }, [name]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(
    initialData?.account ?? null,
  );

  useEffect(() => {
    if (isEditing) return;

    accountsService
      .list()
      .then((accountsList) =>
        setAccounts(Array.isArray(accountsList) ? accountsList : []),
      )
      .catch(() => {
        setAccounts([]);
        onNewTemplateGroupError?.(
          "No se pudieron cargar las cuentas disponibles.",
        );
      });
  }, [isEditing, onNewTemplateGroupError]);

  const disableSend = !name || (!isEditing && !selectedAccount);

  // max_number ya no se envía: el backend lo calcula con los Template Files
  // activos del grupo.
  const sendTemplateGroup = async () => {
    const templateData = isEditing
      ? { name }
      : { name, account_id: selectedAccount?.id ?? null };

    try {
      if (isEditing) {
        await templatesService.updateTemplateGroup(initialData.id, templateData);
      } else {
        await templatesService.createTemplateGroup(templateData);
      }

      if (onTemplateGroupCreated) {
        await onTemplateGroupCreated();
      }
      onClose();
    } catch (err) {
      const errorData = err?.response?.data;
      const accountErrorMessage = Array.isArray(errorData?.account_id)
        ? errorData.account_id[0]
        : errorData?.account_id;

      onNewTemplateGroupError(
        typeof errorData === "string"
          ? errorData
          : accountErrorMessage ||
            errorData?.detail ||
            (isEditing ? "Error al editar el grupo de plantillas." : "Error al crear el grupo de plantillas."),
      );
    }
  };

  const sendDeleteTemplateGroup = async () => {
    try {
      await templatesService.deleteTemplateGroup(initialData.id);
      setShowDeleteConfirm(false);

      if (onTemplateGroupCreated) {
        await onTemplateGroupCreated();
      }
      onClose();
    } catch (err) {
      setShowDeleteConfirm(false);
      onNewTemplateGroupError(
        err?.response?.data?.detail ||
          "Error al eliminar el grupo de plantillas.",
      );
    }
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9998,
        }}
        onClick={onClose}
      />
      {/* Sin ancho fijo: FloatingModal se ajusta al contenido y crece con el
          campo de nombre. */}
      <FloatingModal bottom={128}>
        <div style={{ flexShrink: 0, width: nameFieldWidth, position: "relative" }}>
          <span
            ref={nameSizerRef}
            aria-hidden="true"
            style={{
              position: "absolute",
              visibility: "hidden",
              whiteSpace: "pre",
              fontFamily: "Urbanist",
              fontSize: 13,
            }}
          >
            {name || NAME_PLACEHOLDER}
          </span>
          <BaseInput
            placeholder={NAME_PLACEHOLDER}
            value={name}
            onChange={setName}
          />
        </div>

        <div style={{ flex: "1 1 200px", minWidth: 170 }}>
          <DropdownSelect
            placeholder="Seleccionar cuenta..."
            items={accounts}
            selectedItem={selectedAccount}
            onChange={setSelectedAccount}
            labelKey="name"
            isEditing
            disabled={isEditing}
          />
        </div>

        <Button
          tone="blue"
          iconRight={
            <img
              src={rocketHovered ? rocketHover : rocket}
              alt=""
              style={{ width: 35, height: 35, display: "block" }}
            />
          }
          style={{ flexShrink: 0, minWidth: 90, height: 50 }}
          disabled={disableSend}
          onClick={sendTemplateGroup}
          onMouseEnter={() => setRocketHovered(true)}
          onMouseLeave={() => setRocketHovered(false)}
        >
          {isEditing ? "Guardar Cambios" : "Crear Plantilla"}
        </Button>

        {isEditing && (
          <IconButton
            tone="danger"
            size="smPlus"
            iconSize={20}
            icon={<TrashIcon />}
            className={styles.deleteGroupButton}
            aria-label="Eliminar plantilla"
            onClick={() => setShowDeleteConfirm(true)}
          />
        )}
      </FloatingModal>

      {showDeleteConfirm && (
        <div className={styles.confirmOverlay}>
          <ConfirmDeleteModal
            title="¿Eliminar esta plantilla?"
            description="Esta acción no tiene vuelta atrás. Se eliminará el grupo con todos sus template files."
            name={initialData?.name ?? initialData?.group?.name}
            onConfirm={sendDeleteTemplateGroup}
            onClose={() => setShowDeleteConfirm(false)}
          />
        </div>
      )}
    </>
  );
}

export default TemplateGroupModal;
